import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { ApiError, isTransient } from "./api";
import { SESSION_KEY } from "./session";

/**
 * Client de requêtes des fronts. Un 401 sur n'importe quel écran vaut
 * déconnexion : la session passe à `null` et les gardes renvoient vers la
 * connexion. Hors ligne, une requête part quand même (`networkMode:
 * "always"`) : elle échoue en coupure réseau, retentée puis affichée avec
 * « Réessayer », au lieu d'attendre en pause — squelette sans fin — le retour
 * du réseau ; ce retour relance de lui-même les vues en échec.
 */
export function Providers({ children }: { children: ReactNode }) {
  const [client] = useState(() => {
    const onError = (error: unknown) => {
      if (error instanceof ApiError && error.status === 401) client.setQueryData(SESSION_KEY, null);
    };
    const client: QueryClient = new QueryClient({
      queryCache: new QueryCache({ onError }),
      mutationCache: new MutationCache({ onError }),
      defaultOptions: {
        queries: {
          retry: (count, error) => isTransient(error) && count < 2,
          networkMode: "always",
          // Explicite : avec `networkMode: "always"`, TanStack le désactive par défaut.
          refetchOnReconnect: true,
          refetchOnWindowFocus: false,
        },
      },
    });
    return client;
  });
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
